import { formFieldKeys } from '@messenger/consts'
import { escapeHtml } from '@messenger/utils'
import { nameArrangementSchema } from '@messenger/types'
import { handleString } from '@/utils/tinyMCE/caseDetails/handleString'
import { formatMiddleInitial } from '@/utils/tinyMCE/formatMiddleInitial'
import { NAME_PART_COLUMNS } from '@/utils/tinyMCE/updateDynamicText/arrangeNameWrapper'

/**
 * The name wrapper's children as an insert writes them: one span per part
 * in the case's arrangement when the case has one (a part it hides carries
 * its value, `display:none` and `data-user-hidden`, as the Name Order
 * dialog marks it), else in the standard order; an empty part is a hidden
 * hair space; one space between the visible parts and none at the edges.
 */
export const nameOfDeceasedWrapperInner = (caseDetails) => {
  const nameComponents = [
    { key: formFieldKeys.prefix, format: (value) => value },
    { key: formFieldKeys.firstName, format: (value) => value },
    { key: formFieldKeys.nickName, format: (value) => `"${value}"` },
    {
      key: formFieldKeys.middleName,
      format: (value) => {
        if (!value) return value
        return formatMiddleInitial(value)
      },
    },
    { key: formFieldKeys.maidenName, format: (value) => `(${value})` },
    { key: formFieldKeys.lastName, format: (value) => value },
    { key: formFieldKeys.suffix, format: (value) => value },
  ]

  const arrangement = nameArrangementSchema.safeParse(
    caseDetails.name_arrangement,
  )
  const hiddenColumns = new Set(
    arrangement.success
      ? arrangement.data.hidden.map((part) => NAME_PART_COLUMNS[part])
      : [],
  )
  const orderedComponents = arrangement.success
    ? arrangement.data.order.map((part) =>
        nameComponents.find(
          (component) => component.key === NAME_PART_COLUMNS[part],
        ),
      )
    : nameComponents

  const components = orderedComponents.map((component) => {
    const hasValue = Boolean(caseDetails[component.key])
    const userHidden = hasValue && hiddenColumns.has(component.key)
    const visible = hasValue && !userHidden
    // Each part is escaped on its own: the wrapper is markup, its values text.
    const value = hasValue
      ? escapeHtml(String(component.format(caseDetails[component.key])))
      : '\u200A'

    return {
      hasValue: visible,
      html: `<span class="mceNonEditable" data-case-detail="${component.key}" ${!visible ? 'style="display:none"' : ''}${userHidden ? ' data-user-hidden="true"' : ''}>${value}</span>`,
    }
  })

  // Join with spaces only between visible (hasValue) spans.
  // Hidden spans are placed directly adjacent to the nearest visible span
  // with no space separators to avoid orphaned whitespace on center-aligned text.
  let fullName = ''
  let lastVisibleIndex = -1
  let leadingHidden = ''
  components.forEach((comp, i) => {
    if (comp.hasValue) {
      if (lastVisibleIndex === -1) {
        fullName += leadingHidden + comp.html
      } else {
        // Hidden spans go before next visible span, after the space
        fullName += ' '
        for (let j = lastVisibleIndex + 1; j < i; j++) {
          fullName += components[j].html
        }
        fullName += comp.html
      }
      lastVisibleIndex = i
    } else if (lastVisibleIndex === -1) {
      leadingHidden += comp.html
    }
  })
  if (lastVisibleIndex !== -1) {
    for (let j = lastVisibleIndex + 1; j < components.length; j++) {
      fullName += components[j].html
    }
  } else {
    fullName = components.map((c) => c.html).join('')
  }
  return fullName
}

export const handleNameOfDeceased = (
  editor,
  caseDetails,
  key,
  templates,
  dateFormat,
  selectedTheme,
) => {
  return handleString(
    editor,
    caseDetails,
    key,
    templates,
    dateFormat,
    'Name of Deceased',
    selectedTheme,
    undefined,
    { name_of_deceased: nameOfDeceasedWrapperInner(caseDetails) },
  )
}

export default handleNameOfDeceased
